import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { createStyles, makeStyles } from '@material-ui/core';
import { localizeCurrency, localizeOrdinalNumber } from '@pec/aion-ui-i18next/helpers/localize';
import { IConnectionBillingPreview, ConnectionType } from 'interfaces/index';

interface Props {
  connections: IConnectionBillingPreview[];
  taxes: number;
  total: number;
}

export const SelectedConnectionsList: React.FC<Props> = ({ connections, taxes, total }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const ConnectionTypes = {
    [ConnectionType.Incoming]: t('subscriber:selectedConnectionsList.incoming', 'Incoming'),
    [ConnectionType.Outgoing]: t('subscriber:selectedConnectionsList.outgoing', 'Outgoing'),
    [ConnectionType.Active]: t('subscriber:selectedConnectionsList.active', 'Active')
  };

  const getConnectionName = (connection: IConnectionBillingPreview) => {
    if (connection.type === ConnectionType.Active) {
      return `${localizeOrdinalNumber(connection.ordinal, t).toUpperCase()} ${t(
        'subscriber:common.connection',
        'CONNECTION'
      )} (${connection.organizationName})`;
    }

    return `${localizeOrdinalNumber(connection.ordinal, t).toUpperCase()} ${t(
      'subscriber:common.connection',
      'CONNECTION'
    )}`;
  };

  const billableConnections = connections.filter(
    connection =>
      !connection.isFreeConnection && !connection.isPotentiallyFreeConnection && !connection.isPartiallyFreeConnection
  );
  const potentiallyFreeConnections = connections.filter(
    connection => connection.isPotentiallyFreeConnection || connection.isPartiallyFreeConnection
  );

  return (
    <div className={classes.connectionsList}>
      <div className={classes.billableConnectionsSection}>
        {billableConnections.map(connection => (
          <div
            key={connection.organizationId}
            className={classes.row}
            data-testid={`billable-${connection.organizationId}`}
          >
            <span className={classes.count}>{getConnectionName(connection)}</span>
            <span className={classes.statusCell}>{ConnectionTypes[connection.type]}</span>
            <span className={classes.price}>
              <span className={classes.cell}>{localizeCurrency(connection.price, t)}</span>
            </span>
          </div>
        ))}
      </div>
      {potentiallyFreeConnections.length > 0 && (
        <div className={classes.potentiallyFreeSection}>
          {potentiallyFreeConnections.map(connection => (
            <div
              key={connection.organizationId}
              className={classes.row}
              data-testid={`potentiallyFree-${connection.organizationId}`}
            >
              <span className={classes.count}>{getConnectionName(connection)}</span>
              <span className={classes.statusCell}>{ConnectionTypes[connection.type]}</span>
              <span className={classes.price}>
                <span className={classes.cell}>{localizeCurrency(connection.price, t)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
      <div className={clsx(classes.row, classes.taxesRow)}>
        <div className={classes.cell}>{t('subscriber:selectedConnectionsList.tax', 'TAX')}</div>
        <div className={classes.cell}>{localizeCurrency(taxes, t)}</div>
      </div>
      <div className={clsx(classes.row, classes.totalRow)}>
        <div className={classes.totalCell}>{t('subscriber:selectedConnectionsList.total', 'TOTAL')}</div>
        <div className={classes.totalCell}>{localizeCurrency(total, t)}</div>
      </div>
    </div>
  );
};

const useStyles = makeStyles(() =>
  createStyles({
    connectionsList: {
      border: '1px solid var(--on-surface-outline-000000-12-)',
      borderRadius: '4px'
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '20px 24px 0'
    },
    taxesRow: {
      padding: '24px 24px 14px'
    },
    totalRow: {
      borderTop: '1px solid var(--on-surface-outline-000000-12-)',
      padding: '24px'
    },
    cell: {
      font: 'normal normal 400 16px/24px Roboto',
      color: 'var(--on-surface-high-emphasis-000000-87-)'
    },
    count: {
      font:
        'var(--font-style-normal) normal var(--font-weight-normal) var(--font-size-14)/var(--line-spacing-20) var(--font-family-roboto)',
      letterSpacing: 'var(--character-spacing-0-25)',
      color: '#000000A6'
    },
    cellPotentiallyFree: {
      color: 'var(--text-gray)'
    },
    billableConnectionsSection: {
      paddingBottom: '15px'
    },
    potentiallyFreeSection: {
      backgroundColor: 'var(--green-23-)',
      color: 'var(--dark-green-status-success)',
      borderTop: '2px dashed var(--on-surface-outline-000000-12-)',
      paddingBottom: '15px'
    },
    potentiallyFree: {
      font: 'normal normal 400 16px/24px Roboto',
      color: 'var(--dark-green-status-success)',
      marginRight: '4px'
    },
    price: {
      width: '140px',
      display: 'flex',
      justifyContent: 'end'
    },
    statusCell: {
      flex: 1,
      textAlign: 'right',
      marginRight: '32px',
      color: 'var(--text-gray)'
    },
    totalCell: {
      font: 'normal normal 500 16px/26px Roboto',
      color: 'var(--on-surface-high-emphasis-000000-87-)'
    }
  })
);
